import { useMemo, useState, type CSSProperties } from 'react';
import type {
  ControlSummary,
  EvidenceItem,
  Finding,
  Framework,
  ProductSummary,
  RiskAcceptance,
  Severity,
  Verdict,
} from './api.ts';
import {
  ALL,
  describeScope,
  isNarrowed,
  resolveScope,
  visibleMappings,
  type Scope,
} from './findingScope.ts';
import './results.css';

/**
 * The words the report uses, not the enum names. `INSUFFICIENT_DATA` reads as
 * "Not evidenced" rather than "Unknown" because the thing that is missing is
 * evidence, not the answer — and a reader who sees "unknown" next to a control
 * assumes we failed rather than that we were not permitted to look.
 */
export const VERDICT_LABEL: Record<Verdict, string> = {
  PASS: 'Met',
  FAIL: 'Not met',
  NOT_APPLICABLE: 'Not applicable',
  INSUFFICIENT_DATA: 'Not evidenced',
};

/**
 * The suffix each verdict contributes to every class name that depends on it —
 * the badge in `styles.css`, the card's left edge, the tally bar. One map, so
 * the three cannot disagree about which verdict they are drawing. Anything that
 * renders a verdict, here or in `Drift`, goes through this.
 */
export const VERDICT_KEY: Record<Verdict, string> = {
  PASS: 'met',
  FAIL: 'not-met',
  NOT_APPLICABLE: 'not-applicable',
  INSUFFICIENT_DATA: 'not-evidenced',
};

/**
 * Reason codes in plain English. Identical wording to the rendered report,
 * deliberately: an MSP reads a finding here and then sends the customer the
 * report, and the two saying the same thing differently invites the question of
 * which one is right. `Drift` imports these rather than restating them, because
 * a shared module would mean a file this component does not own.
 */
export const REASON_LABEL: Record<string, string> = {
  unlicensed: 'The tenant’s licence does not expose this configuration',
  unauthorized: 'Our application was not permitted to read this configuration',
  throttled: 'Microsoft Graph throttled the read and the retry budget was exhausted',
  collection_failed: 'The configuration could not be collected',
  source_not_collected: 'This configuration was not part of the scan',
  requires_attestation: 'This depends on a fact only the organisation can supply',
  unsupported_by_api: 'No Microsoft API exposes this; it must be checked by hand',
  no_precondition: 'The thing this control governs does not exist in this tenant',
  capability_not_licensed: 'The capability requires a licence the tenant has not purchased',
  rule_error: 'The control failed to evaluate — this is a defect in the assessment tool',
};

const SEVERITY_RANK: Record<Severity, number> = { critical: 0, high: 1, medium: 2, low: 3 };
const SEVERITIES: readonly Severity[] = ['critical', 'high', 'medium', 'low'];

const SEVERITY_LABEL: Record<Severity, string> = {
  critical: 'Critical',
  high: 'High',
  medium: 'Medium',
  low: 'Low',
};

/**
 * Section order, and what each section is for.
 *
 * Not-evidenced sits directly under the failures and above everything that
 * passed, because it is the section a customer most needs to act on and the one
 * a percentage would have hidden (ADR-0007).
 */
const SECTIONS: ReadonlyArray<{ verdict: Verdict; heading: string; lead: string; hint: string }> = [
  {
    verdict: 'FAIL',
    heading: 'Not met',
    lead: 'The collected configuration does not satisfy these controls.',
    hint: 'the configuration does not satisfy the control',
  },
  {
    verdict: 'INSUFFICIENT_DATA',
    heading: 'Not evidenced',
    lead:
      'Open questions. We could not determine the answer — each one below says why, and what would ' +
      'be needed to settle it. These are neither passes nor failures and are never counted towards ' +
      'a failure total.',
    hint: 'an open question, never counted as a failure',
  },
  {
    verdict: 'PASS',
    heading: 'Met',
    lead: 'The collected configuration satisfies these controls.',
    hint: 'the configuration satisfies the control',
  },
  {
    verdict: 'NOT_APPLICABLE',
    heading: 'Not applicable',
    lead: 'The thing these controls govern does not exist in this tenant.',
    hint: 'nothing in this tenant for the control to govern',
  },
];

const bySeverityThenId = (a: Finding, b: Finding): number =>
  SEVERITY_RANK[a.severity] - SEVERITY_RANK[b.severity] || a.controlId.localeCompare(b.controlId);

const formatMoment = (iso: string): string => {
  const parsed = new Date(iso);
  return Number.isNaN(parsed.getTime()) ? iso : parsed.toLocaleString('en-GB');
};

const toggle = <T,>(active: ReadonlySet<T>, value: T): ReadonlySet<T> => {
  const next = new Set(active);
  if (!next.delete(value)) next.add(value);
  return next;
};

const reasonLead = (verdict: Verdict): string => {
  if (verdict === 'INSUFFICIENT_DATA') return 'Why this could not be assessed';
  if (verdict === 'NOT_APPLICABLE') return 'Why this does not apply';
  return 'Reason';
};

/** Violet for the open questions, neutral for everything else. Never red: the
 *  reason a control could not be assessed is not itself a finding. */
const reasonCalloutClass = (verdict: Verdict): string =>
  verdict === 'INSUFFICIENT_DATA' ? 'callout callout-evidence' : 'callout';

/**
 * A bar whose length is `count × --bar-unit`, capped. Exported as a style object
 * rather than a width so the cap and the unit stay in `styles.css`.
 */
const barLength = (count: number): CSSProperties => ({ '--n': count }) as CSSProperties;

/** The four-tick severity mark. Exactly four inner spans, always. */
export const Sev = ({ severity }: { severity: Severity }) => (
  <span className={`sev sev-${severity}`}>
    <span className="sev-ticks" aria-hidden="true">
      <span />
      <span />
      <span />
      <span />
    </span>
    {SEVERITY_LABEL[severity]}
  </span>
);

/**
 * Evidence values, one per line and never truncated.
 *
 * An engineer has to be able to select a policy GUID or a Graph error string and
 * paste it into a ticket verbatim, so nothing here gets an ellipsis, a max-width
 * or a single-line clamp. Long values wrap; that is the correct behaviour even
 * when it is the ugly one.
 */
export const EvidenceValues = ({ values }: { values: EvidenceItem['values'] }) => {
  if (values.length === 0) return <span className="dw-void">—</span>;
  return (
    <ul className="dw-values">
      {values.map((value, index) => (
        <li key={`${String(value)}#${index}`} className="mono">
          {value === null ? <span className="dw-void">—</span> : String(value)}
        </li>
      ))}
    </ul>
  );
};

interface FindingsProps {
  findings: Finding[];
  controls?: ControlSummary[];
  /** Absent, or empty, hides the product filter rather than offering nothing. */
  products?: ProductSummary[];
  /**
   * Risks this client has accepted. They change nothing here: the verdict is
   * the verdict. The card says so, because somebody reading a failure needs to
   * know it has already been decided about — and whether that decision is still
   * standing or is overdue for review.
   */
  acceptances?: RiskAcceptance[];
  /**
   * The frameworks the scope filter offers. A tenant page passes the ones this
   * client's report covers, so the filter cannot offer a framework the report
   * the customer receives does not mention.
   */
  frameworks?: Framework[];
}

/**
 * The findings of one assessment.
 *
 * Takes its data as props and fetches nothing: the same component renders a
 * real tenant's scan, a fixture-backed demo and a report shared with a client,
 * and a component that fetched would only serve the first of the three.
 *
 * There is no score here and there is not going to be one. A severity-weighted
 * percentage gets screenshotted and quoted out of context, cannot be defended
 * when an auditor asks how it was derived, and buries the not-evidenced
 * findings in a denominator. Counts and severity say the same thing without
 * pretending to a precision nobody has (ADR-0007).
 */
export const Findings = ({
  findings,
  controls,
  products,
  frameworks,
  acceptances,
}: FindingsProps) => {
  const [requestedScope, setRequestedScope] = useState<Scope>(ALL);
  const [verdictFilter, setVerdictFilter] = useState<ReadonlySet<Verdict>>(() => new Set());
  const [severityFilter, setSeverityFilter] = useState<ReadonlySet<Severity>>(() => new Set());
  const [evidenceOverrides, setEvidenceOverrides] = useState<Readonly<Record<string, boolean>>>({});

  const acceptanceOf = useMemo(
    () => new Map((acceptances ?? []).map((entry) => [entry.controlId, entry])),
    [acceptances],
  );

  const controlsById = useMemo(
    () => new Map((controls ?? []).map((control) => [control.id, control] as const)),
    [controls],
  );

  /*
   * Everything below — the tiles, the callouts, the severity counts and the
   * list — is computed over `scoped`, not `findings`. A reader who picks
   * Exchange Online is asking what Exchange looks like, and tiles that kept
   * counting the whole tenant would answer a different question beside the one
   * they asked. The line above the tiles says so, so a screenshot of them
   * cannot pass for the whole assessment.
   */
  const resolved = useMemo(
    () =>
      resolveScope(
        findings,
        { products: products ?? [], controls: controls ?? [], frameworks: frameworks ?? [] },
        requestedScope,
      ),
    [findings, products, controls, frameworks, requestedScope],
  );
  const { scope, options: scopeOptions } = resolved;
  const scoped = resolved.findings;
  const narrowed = isNarrowed(scope);

  const verdictCounts = useMemo(() => {
    const seed: Record<Verdict, number> = {
      PASS: 0,
      FAIL: 0,
      NOT_APPLICABLE: 0,
      INSUFFICIENT_DATA: 0,
    };
    return scoped.reduce(
      (totals, finding) => ({ ...totals, [finding.verdict]: totals[finding.verdict] + 1 }),
      seed,
    );
  }, [scoped]);

  const severityCounts = useMemo(() => {
    const seed: Record<Severity, number> = { critical: 0, high: 0, medium: 0, low: 0 };
    return scoped.reduce(
      (totals, finding) => ({ ...totals, [finding.severity]: totals[finding.severity] + 1 }),
      seed,
    );
  }, [scoped]);

  const attested = useMemo(
    () => scoped.filter((finding) => finding.evidenceSource === 'attested'),
    [scoped],
  );

  const visible = useMemo(
    () =>
      scoped.filter(
        (finding) =>
          (verdictFilter.size === 0 || verdictFilter.has(finding.verdict)) &&
          (severityFilter.size === 0 || severityFilter.has(finding.severity)),
      ),
    [scoped, verdictFilter, severityFilter],
  );

  const filtered = narrowed || verdictFilter.size > 0 || severityFilter.size > 0;

  const clearFilters = () => {
    setRequestedScope(ALL);
    setVerdictFilter(new Set());
    setSeverityFilter(new Set());
  };

  // A framework change clears the clause: a clause belongs to one framework,
  // and carrying `A.5.14` into CIS would select nothing. A product change keeps
  // both, and `resolveScope` drops whichever the product leaves empty.
  const chooseProduct = (value: string) => setRequestedScope({ ...scope, product: value || null });
  const chooseFramework = (value: string) =>
    setRequestedScope({ ...scope, framework: value || null, clause: null });
  const chooseClause = (value: string) => setRequestedScope({ ...scope, clause: value || null });

  // Failures and open questions open their evidence by default; a reader who
  // scrolls past forty passing controls should not have to close forty panels.
  const isOpen = (finding: Finding): boolean =>
    evidenceOverrides[finding.controlId] ??
    (finding.verdict === 'FAIL' || finding.verdict === 'INSUFFICIENT_DATA');

  const setAllEvidence = (open: boolean) =>
    setEvidenceOverrides(Object.fromEntries(findings.map((finding) => [finding.controlId, open])));

  if (findings.length === 0) {
    return (
      <div className="empty">
        <span className="empty-title">No findings</span>
        <p className="empty-body">
          This assessment produced no findings. Nothing in the catalogue could be evaluated against
          the collected configuration.
        </p>
      </div>
    );
  }

  return (
    <div className="dw-results">
      {scopeOptions.products.length > 0 && (
        <div className="toolbar dw-scope" role="group" aria-label="Narrow by product and framework">
          <label className="dw-scope-field">
            <span className="label">Microsoft product</span>
            <select value={scope.product ?? ''} onChange={(e) => chooseProduct(e.target.value)}>
              <option value="">All products ({findings.length})</option>
              {scopeOptions.products.map((option) => (
                <option key={option.id} value={option.id}>
                  {option.label} ({option.count})
                </option>
              ))}
            </select>
          </label>

          {scopeOptions.frameworks.length > 0 && (
            <label className="dw-scope-field">
              <span className="label">Framework</span>
              <select
                value={scope.framework ?? ''}
                onChange={(e) => chooseFramework(e.target.value)}
              >
                <option value="">All frameworks</option>
                {scopeOptions.frameworks.map((option) => (
                  <option key={option.id} value={option.id}>
                    {option.label} ({option.count})
                  </option>
                ))}
              </select>
            </label>
          )}

          {scopeOptions.frameworks.length > 0 && (
            <label className="dw-scope-field dw-scope-clause">
              <span className="label">Clause</span>
              <select
                value={scope.clause ?? ''}
                disabled={scope.framework === null}
                onChange={(e) => chooseClause(e.target.value)}
              >
                <option value="">
                  {scope.framework === null ? 'Choose a framework first' : 'All clauses'}
                </option>
                {scopeOptions.clauses.map((option) => (
                  <option key={option.id} value={option.id}>
                    {option.label} ({option.count})
                  </option>
                ))}
              </select>
            </label>
          )}
        </div>
      )}

      {narrowed && (
        <p className="dw-scope-line" role="status">
          Showing <strong>{describeScope(scope, scopeOptions)}</strong> — {scoped.length} of{' '}
          {findings.length} findings. The counts below are for this selection only.{' '}
          <button type="button" className="btn btn-quiet btn-sm" onClick={() => setRequestedScope(ALL)}>
            Show the whole assessment
          </button>
        </p>
      )}

      <div className="stat-grid" role="group" aria-label="Filter by verdict">
        {SECTIONS.map((section) => {
          const key = VERDICT_KEY[section.verdict];
          const count = verdictCounts[section.verdict];
          return (
            <button
              key={section.verdict}
              type="button"
              className="stat stat-tile"
              aria-pressed={verdictFilter.has(section.verdict)}
              onClick={() => setVerdictFilter(toggle(verdictFilter, section.verdict))}
            >
              <span className={`verdict verdict-${key}`}>{section.heading}</span>
              <span className="stat-figure">{count}</span>
              <span className={`dw-bar dw-bar-${key}`} style={barLength(count)} aria-hidden="true" />
              <span className="stat-hint">{section.hint}</span>
            </button>
          );
        })}
      </div>

      {(verdictCounts.INSUFFICIENT_DATA > 0 || attested.length > 0) && (
        <div className="dw-callout-row">
          {verdictCounts.INSUFFICIENT_DATA > 0 && (
            <div className="callout callout-evidence dw-note">
              <span className="callout-title">
                {verdictCounts.INSUFFICIENT_DATA} control
                {verdictCounts.INSUFFICIENT_DATA === 1 ? '' : 's'} could not be assessed
              </span>
              Usually a licence the tenant has not bought, consent that was revoked, or a question only
              the organisation can answer. Each one below states its cause. Reported as a gap, never as
              a security failure, and never counted towards the failures above.
            </div>
          )}

          {attested.length > 0 && (
            <div className="callout dw-note">
              <span className="callout-title">
                {attested.length} verdict{attested.length === 1 ? '' : 's'} rest
                {attested.length === 1 ? 's' : ''} partly on an attestation
              </span>
              <p>
                These turn on organisational facts no API exposes, so part of the evidence is somebody’s
                answer rather than collected configuration.
              </p>
              <ul className="dw-attested-list">
                {attested.map((finding) => (
                  <li key={finding.controlId}>
                    <span className="mono">{finding.controlId}</span>
                    <span className="attested" />
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      <div className="toolbar dw-filters">
        <span className="label dw-filter-label" id="dw-sev-filter">
          Severity
        </span>
        <div className="dw-chips" role="group" aria-labelledby="dw-sev-filter">
          {SEVERITIES.map((severity) => (
            <button
              key={severity}
              type="button"
              className="dw-chip"
              aria-pressed={severityFilter.has(severity)}
              onClick={() => setSeverityFilter(toggle(severityFilter, severity))}
            >
              <Sev severity={severity} />
              <span className="num dw-chip-count">{severityCounts[severity]}</span>
            </button>
          ))}
        </div>

        <span className="spacer" />

        <button type="button" className="btn btn-quiet btn-sm" onClick={() => setAllEvidence(true)}>
          Expand all evidence
        </button>
        <button type="button" className="btn btn-quiet btn-sm" onClick={() => setAllEvidence(false)}>
          Collapse all
        </button>
        {filtered && (
          <button type="button" className="btn btn-quiet btn-sm" onClick={clearFilters}>
            Clear filters
          </button>
        )}
      </div>

      <p className="status-inline dw-count" role="status">
        {filtered
          ? `Showing ${visible.length} of ${findings.length} findings.`
          : `${findings.length} findings.`}
      </p>

      {visible.length === 0 ? (
        <div className="empty">
          <span className="empty-title">No findings match these filters</span>
          <p className="empty-body">
            All {findings.length} findings are still here — the filters above have hidden them.
          </p>
          <p className="dw-empty-action">
            <button type="button" className="btn btn-quiet btn-sm" onClick={clearFilters}>
              Clear filters
            </button>
          </p>
        </div>
      ) : (
        SECTIONS.map((section) => {
          const group = visible
            .filter((finding) => finding.verdict === section.verdict)
            .sort(bySeverityThenId);
          if (group.length === 0) return null;
          const key = VERDICT_KEY[section.verdict];

          return (
            <section
              key={section.verdict}
              className="dw-section"
              aria-labelledby={`section-${section.verdict}`}
            >
              <h3 id={`section-${section.verdict}`} className="dw-section-heading">
                <span className={`verdict verdict-${key}`}>{section.heading}</span>
                <span className="dw-section-count num">
                  {group.length} {group.length === 1 ? 'control' : 'controls'}
                </span>
              </h3>
              {/* Not-evidenced is set apart from the verdict sections either side,
                  because it is a different kind of answer rather than a milder
                  failure. Violet, never a tint of red. */}
              {section.verdict === 'INSUFFICIENT_DATA' ? (
                <p className="callout callout-evidence dw-section-lead">{section.lead}</p>
              ) : (
                <p className="note dw-section-lead">{section.lead}</p>
              )}
              {group.map((finding) => (
                <FindingCard
                  key={finding.controlId}
                  finding={finding}
                  control={controlsById.get(finding.controlId)}
                  acceptance={acceptanceOf.get(finding.controlId) ?? null}
                  frameworks={frameworks}
                  open={isOpen(finding)}
                  onToggle={() =>
                    setEvidenceOverrides({
                      ...evidenceOverrides,
                      [finding.controlId]: !isOpen(finding),
                    })
                  }
                />
              ))}
            </section>
          );
        })
      )}
    </div>
  );
};

interface CardProps {
  finding: Finding;
  control: ControlSummary | undefined;
  acceptance?: RiskAcceptance | null;
  /** The frameworks this client's report covers. Absent shows every mapping. */
  frameworks?: Framework[];
  open: boolean;
  onToggle: () => void;
}

const FindingCard = ({ finding, control, acceptance, frameworks, open, onToggle }: CardProps) => {
  const panelId = `dw-evidence-${finding.controlId}`;
  const key = VERDICT_KEY[finding.verdict];
  const mappings = visibleMappings(control?.mappings ?? [], frameworks);
  const parameters = Object.entries(finding.parameters);
  const narrative = finding.narrative;
  const isAttested = finding.evidenceSource === 'attested';
  // Only a failure can be covered, and an acceptance past its review date is
  // shown as overdue rather than removed: a lapsed decision is still a decision
  // somebody made, and hiding it would lose the name attached to it.
  const accepted = finding.verdict === 'FAIL' ? (acceptance ?? null) : null;
  const overdue = accepted !== null && accepted.reviewBy < new Date().toISOString().slice(0, 10);

  return (
    <article className={`card dw-finding dw-rail-${key}`}>
      <div className="card-head dw-finding-head">
        <div className="dw-ident">
          <span className="label mono dw-control-id">{finding.controlId}</span>
          <h4 className={control ? 'section-title' : 'section-title mono'}>
            {control?.title ?? finding.controlId}
          </h4>
          {mappings.length > 0 && (
            <ul className="dw-maps">
              {mappings.map((mapping) => (
                <li key={`${mapping.framework}-${mapping.criterion}`}>
                  {mapping.framework} {mapping.criterion}
                </li>
              ))}
            </ul>
          )}
        </div>
        <div className="dw-marks">
          <span className={`verdict verdict-${key}`}>{VERDICT_LABEL[finding.verdict]}</span>
          {isAttested && <span className="attested" />}
          <Sev severity={finding.severity} />
        </div>
      </div>

      <div className="card-body">
        {accepted && (
          <div className={`callout dw-note${overdue ? ' callout-caution' : ''}`}>
            <span className="callout-title">
              {overdue
                ? 'Accepted by the client — review overdue'
                : 'Accepted by the client'}
            </span>
            {accepted.reason}
            <p className="dw-void">
              {accepted.acceptedBy}, review {overdue ? 'was due' : 'due'} {accepted.reviewBy}. The
              verdict is unchanged; an accepted control raises no alert until this date passes.
            </p>
          </div>
        )}

        {isAttested && (
          <p className="callout dw-note">
            Part of this verdict rests on an answer given by the organisation rather than on
            collected configuration. Everything that is observable was still checked.
          </p>
        )}

        {finding.reasonCode && (
          <div className={`${reasonCalloutClass(finding.verdict)} dw-note`}>
            <span className="callout-title">{reasonLead(finding.verdict)}</span>
            {REASON_LABEL[finding.reasonCode] ?? finding.reasonCode}
          </div>
        )}

        {narrative ? (
          <div className="dw-narrative">
            <p className="lead">{narrative.summary}</p>
            <h5 className="label">Why it matters</h5>
            <p>{narrative.whyItMatters}</p>
            <h5 className="label">Risk in this tenant</h5>
            <p>{narrative.risk}</p>
            {narrative.remediation.length > 0 && (
              <>
                {/* Instructions, not buttons. DriftWatch reads the tenant and
                    never writes to it, so the engineer performs these in the
                    Microsoft admin centre. */}
                <h5 className="label">Remediation — carry these out in the Microsoft admin centre</h5>
                <ol className="dw-steps">
                  {narrative.remediation.map((step) => (
                    <li key={step.action}>
                      {step.action}
                      {step.where && <span className="mono dw-where">{step.where}</span>}
                    </li>
                  ))}
                </ol>
              </>
            )}
          </div>
        ) : (
          // Null narrative is the ordinary state when the language model is off,
          // so it reads as the control's own description and not as a failure.
          control?.intent && <p className="dw-intent">{control.intent}</p>
        )}

        <button
          type="button"
          className="btn btn-quiet btn-sm dw-evidence-toggle"
          aria-expanded={open}
          aria-controls={panelId}
          onClick={onToggle}
        >
          <span className="dw-caret" aria-hidden="true" />
          {finding.evidence.length === 0
            ? 'Evidence (none recorded)'
            : `Evidence (${finding.evidence.length} item${finding.evidence.length === 1 ? '' : 's'})`}
        </button>

        <div id={panelId} className="dw-evidence" hidden={!open}>
          {finding.evidence.length === 0 ? (
            <p className="note">No evidence was recorded for this finding.</p>
          ) : (
            <dl className="dw-pairs">
              {finding.evidence.map((item) => (
                <div key={item.label} className="dw-pair">
                  <dt>
                    <span className="dw-pair-label">{item.label}</span>
                    <span className="dw-evclass">{item.class}</span>
                  </dt>
                  <dd>
                    <EvidenceValues values={item.values} />
                    {item.note && <p className="note dw-pair-note">{item.note}</p>}
                  </dd>
                </div>
              ))}
            </dl>
          )}

          <dl className="dw-pairs dw-provenance">
            <div className="dw-pair">
              <dt>
                <span className="dw-pair-label">Decided by</span>
              </dt>
              <dd className="mono">
                {finding.controlId}@{finding.ruleVersion}
              </dd>
            </div>
            <div className="dw-pair">
              <dt>
                <span className="dw-pair-label">Evidence source</span>
              </dt>
              <dd>
                {isAttested ? 'Collected configuration and an attestation' : 'Collected configuration'}
              </dd>
            </div>
            <div className="dw-pair">
              <dt>
                <span className="dw-pair-label">Observed</span>
              </dt>
              <dd className="num">{formatMoment(finding.observedAt)}</dd>
            </div>
            {parameters.length > 0 && (
              <div className="dw-pair">
                <dt>
                  <span className="dw-pair-label">Thresholds applied</span>
                </dt>
                <dd>
                  <EvidenceValues
                    values={parameters.map(([name, value]) => `${name} = ${String(value)}`)}
                  />
                </dd>
              </div>
            )}
            {narrative && (
              <div className="dw-pair">
                <dt>
                  <span className="dw-pair-label">Explanation written by</span>
                </dt>
                <dd>
                  {narrative.modelId ?? narrative.source} — explanatory text only. The verdict above
                  was decided by the rule, not by the explanation.
                </dd>
              </div>
            )}
          </dl>
        </div>
      </div>
    </article>
  );
};
